/**
 * Supabase GoTrue auth helpers shared by Tenant + Admin SPAs.
 *
 * Supabase Cloud rejects every call to `/auth/v1/*` (token, refresh, recover,
 * user, …) that does not carry an `apikey` header. The legacy on-prem GoTrue
 * accepted the call without one, so the migration to Cloud silently broke
 * email+password login on every SPA that talks to GoTrue directly. Google
 * OAuth keeps working because the apikey is appended by GoTrue on the
 * `/authorize` redirect.
 *
 * The key reaches the SPA at START, not at build: `index.html` loads
 * `/runtime-config` (served by the backend from
 * `platform_settings.supabase_anon_key`, see
 * `bolloprint_shared/spa_runtime_config.py`), which sets
 * `window.__BP_RUNTIME__` before the module bundle runs. Until 2026-09-18 it
 * was baked in with `VITE_SUPABASE_ANON_KEY`, which made every image specific
 * to one environment — so the image promoted to production was never the one
 * that ran in dev. The value is public — it is meant to live in client JS —
 * but it MUST match the Cloud project the nginx relay points at, which is
 * exactly why it comes from the environment's own database. The publishable
 * key (`sb_publishable_…`) and the legacy anon JWT both work.
 *
 * If it is empty we log once and let the call proceed; GoTrue will return 401
 * with "No API key found in request" — easier to diagnose than a silent header
 * drop.
 */

interface BpRuntimeConfig {
  supabaseAnonKey?: string;
}

function readAnonKey(): string {
  if (typeof window === "undefined") return "";
  const runtime = (window as unknown as { __BP_RUNTIME__?: BpRuntimeConfig }).__BP_RUNTIME__;
  return runtime?.supabaseAnonKey || "";
}

let warnedMissing = false;
function warnIfMissing(key: string): void {
  if (!key && !warnedMissing) {
    warnedMissing = true;
    // eslint-disable-next-line no-console
    console.warn(
      "[supabaseAuth] Supabase anon key is empty — /auth/v1/* calls will 401. /runtime-config reads it from platform_settings.supabase_anon_key (Admin → Configuración → Supabase).",
    );
  }
}

/** The anon/publishable key for the SPA's Supabase project, or "" if unset. */
export function getSupabaseAnonKey(): string {
  const key = readAnonKey();
  warnIfMissing(key);
  return key;
}

/**
 * Build a Headers object for an unauthenticated /auth/v1 call (login, refresh,
 * recover). Always JSON; always carries `apikey` when the runtime config has it.
 */
export function authV1Headers(extra?: Record<string, string>): Record<string, string> {
  const key = getSupabaseAnonKey();
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (key) h.apikey = key;
  if (extra) Object.assign(h, extra);
  return h;
}
